(() => {
  const PROFILE_KEY = 'zoo_pet_profile_v1';

  const asArray = (value) => {
    if (value == null || value === '') return [];
    if (Array.isArray(value)) return value.flatMap(asArray);
    return String(value).split(/[|;,]/).map((item) => item.trim()).filter(Boolean);
  };

  const normalize = (value) => asArray(value)
    .join(' ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9ß\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const hasAny = (text, terms) => terms.some((term) => text.includes(normalize(term)));

  /* Tierarten. url = Zielkollektion im Ergebnis; null fällt auf den in Liquid
     gesetzten Link zurück (Section-Einstellung „Ziel“). */
  const SPECIES = {
    dog: { label: 'Hunde', terms: ['hund', 'dog'], url: '/collections/hundefutter-snacks' },
    cat: { label: 'Katzen', terms: ['katze', 'cat'], url: '/collections/katzenfutter-snacks' },
    horse: { label: 'Pferde', terms: ['pferd', 'pony', 'horse', 'equine'], url: null }
  };

  const NEEDS = {
    sensitive: { label: 'Sensibler Bauch', terms: ['sensitiv', 'sensitive', 'sensible', 'verträglich', 'vertraeglich', 'schonkost'] },
    weight: { label: 'Gewicht im Blick', terms: ['gewicht', 'weight', 'light', 'fettarm', 'fettreduziert', 'gewichtsreduktion'] },
    grainfree: { label: 'Getreidefrei', terms: ['getreidefrei', 'grain free', 'ohne getreide'] },
    vegan: { label: 'Vegan', terms: ['vegan', 'pflanzlich'] },
    highprotein: { label: 'High Protein', terms: ['high protein', 'high-protein', 'proteinreich'] },
    gastro: { label: 'Magen & Darm', terms: ['gastro', 'magen', 'darm', 'verdauung', 'intestinal'] },
    urinary: { label: 'Harnwege', terms: ['urinary', 'harnweg', 'struvit'] },
    renal: { label: 'Niere', terms: ['renal', 'niere', 'nieren'] }
  };

  const STRICT_NEEDS = new Set(['grainfree', 'vegan', 'gastro', 'urinary', 'renal']);

  const PROTEINS = {
    poultry: { label: 'Geflügel', terms: ['huhn', 'hühner', 'huehner', 'hähnchen', 'haehnchen', 'chicken', 'pute', 'truthahn', 'ente', 'gans', 'geflügel', 'gefluegel'] },
    beef: { label: 'Rind oder Kalb', terms: ['rind', 'beef', 'kalb'] },
    lamb: { label: 'Lamm oder Schaf', terms: ['lamm', 'schaf'] },
    horse: { label: 'Pferd', terms: ['pferd'] },
    fish: { label: 'Fisch', terms: ['fisch', 'lachs', 'forelle', 'thunfisch', 'hering', 'kabeljau'] },
    game: { label: 'Wild', terms: ['wild', 'hirsch', 'reh', 'wildschwein'] },
    kangaroo: { label: 'Känguru', terms: ['känguru', 'kaenguru', 'kangaroo'] }
  };

  const LIFE = {
    junior: { label: 'Junior', terms: ['welpe', 'puppy', 'junior', 'kitten', 'kids', 'jungtier', 'fohlen', 'foal'] },
    adult: { label: 'Erwachsen', terms: ['adult', 'erwachsen'] },
    senior: { label: 'Senior', terms: ['senior', 'ältere', 'aeltere'] }
  };

  const ALL_LIFE_TERMS = ['alle lebensphasen', 'all life stages', 'all ages', 'für jedes alter', 'fuer jedes alter'];
  const SNACK_TERMS = ['snack', 'leckerli', 'kauartikel', 'kausnack', 'belohnung', 'keks', 'sticks', 'filet', 'creme'];
  const COMPLETE_TERMS = ['alleinfuttermittel', 'alleinfutter', 'vollnahrung'];
  const SUPPLEMENTARY_TERMS = [
    'ergänzungsfuttermittel', 'ergaenzungsfuttermittel',
    'ergänzungsfutter', 'ergaenzungsfutter',
    'ergänzunsfutter', 'ergaenzunsfutter',
    'einzelfuttermittel'
  ];

  const safeProfileRead = () => {
    try { return JSON.parse(window.localStorage.getItem(PROFILE_KEY) || 'null'); } catch (_) { return null; }
  };

  const safeProfileWrite = (profile) => {
    try { window.localStorage.setItem(PROFILE_KEY, JSON.stringify(profile)); } catch (_) {}
  };

  const safeProfileClear = () => {
    try { window.localStorage.removeItem(PROFILE_KEY); } catch (_) {}
  };

  const getChecked = (form, prefix) => Array.from(form.querySelectorAll(`input[name^="${prefix}"]:checked`)).map((input) => input.value);

  const createReason = (kind, code, text) => ({ kind, code, text });

  const evaluate = (data, profile) => {
    const reasons = [];
    /* Ist die Tierart als Metafeld gepflegt, zählt nur die. Sonst Notbehelf aus Typ,
       Tags und Titel — sonst würde „Pferd“ als Proteinquelle die Tierart verfälschen. */
    const animalDeclared = normalize(data.animal);
    const animalText = animalDeclared || normalize([data.productType, data.tags, data.title]);
    const lifeText = normalize([data.lifeStages, data.tags, data.title]);
    const feedCategoryText = normalize(data.feedCategory);
    const productContextText = normalize([data.foodForm, data.productType, data.tags, data.title]);
    const needsText = normalize([data.diet, data.specialRequirements, data.freeFrom, data.feedCategory, data.foodForm, data.productType, data.tags, data.title]);
    const proteinText = normalize([data.proteinSource, data.ingredients, data.tags, data.title]);

    const species = SPECIES[profile.species] || SPECIES.dog;
    const animalMatches = hasAny(animalText, species.terms);
    const otherAnimalMatches = Object.keys(SPECIES)
      .filter((key) => key !== profile.species)
      .some((key) => hasAny(animalText, SPECIES[key].terms));
    const animalContradicts = !animalMatches && otherAnimalMatches;

    if (animalMatches) reasons.push(createReason('positive', 'animal_match', `Für ${species.label} ausgewiesen.`));
    else if (animalContradicts) reasons.push(createReason('blocker', 'animal_mismatch', `Dieses Produkt ist nicht für ${species.label} ausgewiesen.`));
    else reasons.push(createReason('unknown', 'animal_missing', 'Die Tierart ist in den Produktfeldern nicht eindeutig gepflegt.'));

    const allLife = hasAny(lifeText, ALL_LIFE_TERMS);
    const lifeMatches = allLife || hasAny(lifeText, LIFE[profile.life].terms);
    const anyKnownLife = Object.values(LIFE).some((item) => hasAny(lifeText, item.terms));
    if (lifeMatches) reasons.push(createReason('positive', 'life_match', `Für die Lebensphase „${LIFE[profile.life].label}“ ausgewiesen.`));
    else if (anyKnownLife) reasons.push(createReason('blocker', 'life_mismatch', `Die gepflegte Lebensphase passt nicht zu „${LIFE[profile.life].label}“.`));
    else reasons.push(createReason('unknown', 'life_missing', 'Für dieses Produkt ist keine eindeutige Lebensphase hinterlegt.'));

    const isSnack = hasAny(productContextText, SNACK_TERMS);
    const isComplete = hasAny(feedCategoryText, COMPLETE_TERMS);
    const isSupplementary = hasAny(feedCategoryText, SUPPLEMENTARY_TERMS);
    if (profile.intent === 'complete') {
      if (isComplete) reasons.push(createReason('positive', 'intent_complete_match', 'Im Feld „Futtermittelart“ als Alleinfutter ausgewiesen.'));
      else if (isSupplementary) reasons.push(createReason('blocker', 'intent_complete_mismatch', 'Als Ergänzungsfutter gepflegt und damit nicht zur alleinigen täglichen Fütterung vorgesehen.'));
      else if (isSnack) reasons.push(createReason('blocker', 'intent_complete_mismatch', 'Als Snack erkennbar, aber nicht als Alleinfutter ausgewiesen.'));
      else reasons.push(createReason('unknown', 'intent_missing', 'Die Eignung als Alleinfutter ist im Feld „Futtermittelart“ nicht eindeutig gepflegt.'));
    } else if (profile.intent === 'supplementary') {
      if (isSupplementary) reasons.push(createReason('positive', 'intent_supplementary_match', 'Im Feld „Futtermittelart“ als Ergänzungsfutter ausgewiesen.'));
      else if (isComplete) reasons.push(createReason('blocker', 'intent_supplementary_mismatch', 'Als Alleinfutter gepflegt, nicht als Ergänzungsfutter.'));
      else reasons.push(createReason('unknown', 'intent_missing', 'Die Eignung als Ergänzungsfutter ist im Feld „Futtermittelart“ nicht eindeutig gepflegt.'));
    } else if (profile.intent === 'snack') {
      if (isSnack) reasons.push(createReason('positive', 'intent_snack_match', 'Als Snack oder Belohnung erkennbar.'));
      else if (isComplete) reasons.push(createReason('warning', 'intent_snack_warning', 'Als Alleinfutter gepflegt, nicht ausdrücklich als Snack.'));
      else if (isSupplementary) reasons.push(createReason('warning', 'intent_snack_warning', 'Als Ergänzungsfutter gepflegt, aber nicht ausdrücklich als Snack.'));
      else reasons.push(createReason('unknown', 'intent_missing', 'Die Eignung als Snack ist in den Produktfeldern nicht eindeutig gepflegt.'));
    } else {
      if (isComplete) reasons.push(createReason('positive', 'intent_category_known', 'Als Alleinfutter ausgewiesen.'));
      else if (isSupplementary) reasons.push(createReason('positive', 'intent_category_known', 'Als Ergänzungsfutter ausgewiesen.'));
      else if (isSnack) reasons.push(createReason('positive', 'intent_category_known', 'Als Snack oder Belohnung erkennbar.'));
      else reasons.push(createReason('unknown', 'intent_missing', 'Die Futtermittelart ist in den Produktfeldern nicht eindeutig gepflegt.'));
    }

    profile.needs.filter((need) => need !== 'none').forEach((need) => {
      const definition = NEEDS[need];
      if (!definition) return;
      if (hasAny(needsText, definition.terms)) {
        reasons.push(createReason('positive', `need_${need}_match`, `Merkmal „${definition.label}“ ist in den Produktfeldern erkennbar.`));
      } else if (STRICT_NEEDS.has(need)) {
        reasons.push(createReason('blocker', `need_${need}_mismatch`, `Das Produkt ist nicht ausdrücklich für „${definition.label}“ ausgewiesen.`));
      } else {
        reasons.push(createReason('warning', `need_${need}_warning`, `„${definition.label}“ ist in den Produktfeldern nicht bestätigt.`));
      }
    });

    profile.avoid.filter((protein) => protein !== 'none').forEach((protein) => {
      const definition = PROTEINS[protein];
      if (!definition) return;
      if (!proteinText) {
        reasons.push(createReason('unknown', `protein_${protein}_missing`, `Proteinangaben fehlen; „${definition.label}“ kann nicht sicher geprüft werden.`));
      } else if (hasAny(proteinText, definition.terms)) {
        reasons.push(createReason('blocker', `protein_${protein}_found`, `In den Produktangaben wurde „${definition.label}“ erkannt.`));
      } else {
        reasons.push(createReason('positive', `protein_${protein}_not_found`, `„${definition.label}“ wurde in den gepflegten Angaben nicht erkannt.`));
      }
    });

    const blockers = reasons.filter((reason) => reason.kind === 'blocker');
    const unknowns = reasons.filter((reason) => reason.kind === 'unknown');
    const warnings = reasons.filter((reason) => reason.kind === 'warning');
    let state = 'good';
    if (blockers.length) state = 'no';
    else if (unknowns.length) state = 'unknown';
    else if (warnings.length) state = 'conditional';

    return { state, reasons };
  };

  const resultCopy = {
    good: { badge: '🐾 Napf-Match', title: 'Sieht nach einem Match aus!', summary: 'Alle geprüften Produktmerkmale passen zu deinen Angaben.' },
    conditional: { badge: '💛 Fast ein Match', title: 'Passt mit einem Hinweis', summary: 'Die Basis stimmt. Mindestens ein gewünschtes Merkmal ist aber nicht eindeutig bestätigt.' },
    unknown: { badge: '🔎 Noch offen', title: 'Noch nicht eindeutig', summary: 'Für eine klare Aussage fehlen einzelne Produktangaben. Wir behaupten hier lieber nicht zu viel.' },
    no: { badge: '🛑 Kein Napf-Match', title: 'Passt eher nicht', summary: 'Mindestens ein wichtiges Produktmerkmal widerspricht deinen Angaben.' }
  };

  const factValues = (data) => [
    data.animal,
    data.lifeStages,
    data.feedCategory,
    data.foodForm,
    data.proteinSource,
    data.freeFrom,
    data.diet,
    data.specialRequirements,
    data.energy
  ].flatMap(asArray).filter(Boolean).slice(0, 9);

  const pushAnalytics = (data, result) => {
    const payload = {
      event: 'zoo_product_fit_result',
      product_id: String(data.productId || ''),
      product_handle: data.handle || '',
      fit_result: result.state,
      fit_rule_ids: result.reasons.map((reason) => reason.code)
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent('zoo:product-fit-result', { detail: payload }));
  };

  const init = (root) => {
    if (root.dataset.zooFitReady === 'true') return;
    root.dataset.zooFitReady = 'true';

    const dataNode = root.querySelector('[data-zoo-product-fit-data]');
    const form = root.querySelector('[data-zoo-fit-form]');
    const steps = Array.from(root.querySelectorAll('[data-zoo-fit-step]'));
    const resultNode = root.querySelector('[data-zoo-fit-result]');
    const resultLink = root.querySelector('[data-zoo-fit-result-link]');
    if (!dataNode || !form || !steps.length || !resultNode) return;

    let data;
    try { data = JSON.parse(dataNode.textContent); } catch (_) { return; }
    let currentStep = 0;

    /* Aus Liquid gesetzte Werte als Fallback merken (Section-Einstellungen). */
    const defaultLinkHref = resultLink ? resultLink.getAttribute('href') : '';
    const defaultLinkText = resultLink ? resultLink.textContent.trim() : '';

    const progressLabel = root.querySelector('[data-zoo-fit-progress-label]');
    const progressName = root.querySelector('[data-zoo-fit-progress-name]');
    const progressBar = root.querySelector('[data-zoo-fit-progress-bar]');
    const paws = Array.from(root.querySelectorAll('.zoo-fit-check__paws span'));

    const showStep = (index) => {
      currentStep = Math.max(0, Math.min(index, steps.length - 1));
      steps.forEach((step, stepIndex) => { step.hidden = stepIndex !== currentStep; });
      progressLabel.textContent = `Frage ${currentStep + 1} von ${steps.length}`;
      progressName.textContent = steps[currentStep].dataset.stepName || '';
      progressBar.style.width = `${((currentStep + 1) / steps.length) * 100}%`;
      paws.forEach((paw, pawIndex) => paw.classList.toggle('is-active', pawIndex <= currentStep));
    };

    /* Über den Namenspräfix, nicht über die Produkt-ID — die Feldnamen können auch die
       Section-ID tragen, wenn mehrere Instanzen auf einer Seite stehen. */
    const restore = () => {
      const profile = safeProfileRead();
      if (!profile) return;
      const wanted = {
        zoo_fit_species_: [profile.species],
        zoo_fit_life_: [profile.life],
        zoo_fit_intent_: [profile.intent],
        zoo_fit_needs_: profile.needs,
        zoo_fit_avoid_: profile.avoid
      };
      Object.keys(wanted).forEach((prefix) => {
        const values = wanted[prefix];
        if (!Array.isArray(values)) return;
        form.querySelectorAll(`input[name^="${prefix}"]`).forEach((input) => { input.checked = values.includes(input.value); });
      });
    };

    root.querySelectorAll('[data-exclusive-group]').forEach((group) => {
      group.addEventListener('change', (event) => {
        const input = event.target.closest('input[type="checkbox"]');
        if (!input) return;
        const exclusive = group.querySelector('[data-exclusive]');
        const regular = Array.from(group.querySelectorAll('input[type="checkbox"]:not([data-exclusive])'));
        if (input.hasAttribute('data-exclusive') && input.checked) regular.forEach((item) => { item.checked = false; });
        if (!input.hasAttribute('data-exclusive') && input.checked && exclusive) exclusive.checked = false;
        if (exclusive && !exclusive.checked && !regular.some((item) => item.checked)) exclusive.checked = true;
      });
    });

    form.addEventListener('click', (event) => {
      const next = event.target.closest('[data-zoo-fit-next]');
      const back = event.target.closest('[data-zoo-fit-back]');
      if (next) {
        const required = steps[currentStep].querySelector('input[required]');
        if (required && !form.querySelector(`input[name="${required.name}"]:checked`)) {
          required.reportValidity();
          return;
        }
        showStep(currentStep + 1);
      }
      if (back) showStep(currentStep - 1);
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const profile = {
        species: getChecked(form, 'zoo_fit_species_')[0],
        life: getChecked(form, 'zoo_fit_life_')[0],
        intent: getChecked(form, 'zoo_fit_intent_')[0],
        needs: getChecked(form, 'zoo_fit_needs_'),
        avoid: getChecked(form, 'zoo_fit_avoid_')
      };
      if (!profile.species || !profile.life || !profile.intent) return;
      safeProfileWrite(profile);

      const result = evaluate(data, profile);
      const copy = resultCopy[result.state];
      resultNode.dataset.state = result.state;
      resultNode.querySelector('[data-zoo-fit-result-badge]').textContent = copy.badge;
      resultNode.querySelector('[data-zoo-fit-result-title]').textContent = copy.title;
      resultNode.querySelector('[data-zoo-fit-result-summary]').textContent = copy.summary;

      if (resultLink) {
        const isNoMatch = result.state === 'no';
        const speciesDefinition = SPECIES[profile.species];
        const discoveryUrl = (speciesDefinition && speciesDefinition.url) || defaultLinkHref;
        resultLink.textContent = isNoMatch ? 'Zum Futterberater' : (defaultLinkText || 'Weitere Produkte entdecken');
        resultLink.setAttribute('href', isNoMatch ? '/pages/futterberater' : discoveryUrl);
      }

      const list = resultNode.querySelector('[data-zoo-fit-result-reasons]');
      list.textContent = '';
      const order = { blocker: 0, warning: 1, unknown: 2, positive: 3 };
      result.reasons.sort((a, b) => order[a.kind] - order[b.kind]).slice(0, 8).forEach((reason) => {
        const item = document.createElement('li');
        item.dataset.kind = reason.kind;
        const mark = document.createElement('span');
        mark.className = 'zoo-fit-reason__mark';
        mark.textContent = reason.kind === 'positive' ? '✓' : reason.kind === 'blocker' ? '×' : '!';
        const text = document.createElement('span');
        text.textContent = reason.text;
        item.append(mark, text);
        list.append(item);
      });

      const facts = resultNode.querySelector('[data-zoo-fit-result-facts]');
      facts.textContent = '';
      factValues(data).forEach((fact) => {
        const chip = document.createElement('span');
        chip.textContent = fact;
        facts.append(chip);
      });

      form.hidden = true;
      root.querySelector('.zoo-fit-check__progress').hidden = true;
      resultNode.hidden = false;
      resultNode.focus?.({ preventScroll: true });
      pushAnalytics(data, result);
    });

    root.querySelector('[data-zoo-fit-restart]').addEventListener('click', () => {
      safeProfileClear();
      form.reset();
      form.querySelectorAll('[data-exclusive]').forEach((input) => { input.checked = true; });
      resultNode.hidden = true;
      form.hidden = false;
      root.querySelector('.zoo-fit-check__progress').hidden = false;
      showStep(0);
    });

    restore();
    showStep(0);
  };

  const initAll = () => document.querySelectorAll('[data-zoo-product-fit]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
